import React, { useState, useEffect } from 'react';
import { db, storage } from '../../services/firebase';
import { collection, query, orderBy, onSnapshot, doc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Megaphone, Plus, Trash2, Edit, Save, X, Image as ImageIcon, CheckCircle, Siren, ToggleLeft, ToggleRight } from 'lucide-react';
import { Announcement } from '../../types';

export const AnnouncementManager: React.FC = () => {
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState<string | null>(null);
    const [title, setTitle] = useState('');
    const [text, setText] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [expiresDays, setExpiresDays] = useState(7); // Default 7 days

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    useEffect(() => {
        if (!db) return;
        const q = query(collection(db, 'announcements'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Announcement));
            setAnnouncements(list);
        });
        return () => unsubscribe();
    }, []);

    const resetForm = () => {
        setIsEditing(false);
        setCurrentId(null);
        setTitle('');
        setText('');
        setImageUrl('');
        setIsActive(true);
        setExpiresDays(7);
        setError(null);
        setUploadProgress(0);
    };

    const handleEdit = (ann: Announcement) => {
        setIsEditing(true);
        setCurrentId(ann.id);
        setTitle(ann.title);
        setText(ann.text);
        setImageUrl(ann.imageUrl || '');
        setIsActive(ann.isActive);

        // Calculate days left for the form
        if (ann.expiresAt) {
            const ms = ann.expiresAt.toMillis() - Date.now();
            const days = Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
            setExpiresDays(days);
        } else {
            setExpiresDays(7);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Deseja realmente excluir este aviso?')) return;
        if (!db) return;
        try {
            await deleteDoc(doc(db, 'announcements', id));
        } catch (err: any) {
            console.error(err);
            alert("Erro ao excluir: " + err.message);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!db) return;
        if (!title.trim() || !text.trim()) {
            setError("Título e Texto são obrigatórios.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const id = currentId || `ann_${Date.now()}`;
            const docRef = doc(db, 'announcements', id);

            const expDate = new Date();
            expDate.setDate(expDate.getDate() + expiresDays);

            const payload: any = {
                title: title.trim(),
                text: text.trim(),
                imageUrl: imageUrl.trim() || null,
                isActive,
                expiresAt: Timestamp.fromDate(expDate)
            };

            // Only set createdAt if it's new
            if (!currentId) {
                payload.createdAt = Timestamp.now();
            }

            await setDoc(docRef, payload, { merge: true });
            resetForm();
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Erro ao salvar o aviso.');
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0 || !storage) return;
        const file = e.target.files[0];
        const storageRef = ref(storage, `announcements/img_${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
            },
            (error) => {
                console.error("Upload error", error);
                alert("Erro ao enviar imagem.");
                setUploadProgress(0);
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    setImageUrl(downloadURL);
                    setUploadProgress(0);
                });
            }
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center border-b border-noir-800 pb-4">
                <h1 className="text-2xl font-bold font-mono tracking-widest text-noir-accent flex items-center gap-3">
                    <Megaphone className="w-6 h-6" /> QUADRO DE AVISOS
                </h1>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="bg-noir-800 hover:bg-noir-700 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors font-bold uppercase text-sm"
                    >
                        <Plus className="w-4 h-4" /> Novo Aviso
                    </button>
                )}
            </div>

            {isEditing ? (
                <div className="bg-noir-900 border border-noir-800 rounded-lg p-6 animate-in fade-in zoom-in-95 duration-200">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-lg font-bold flex items-center gap-2">
                            {currentId ? <Edit className="w-5 h-5 text-amber-500" /> : <Plus className="w-5 h-5 text-green-500" />}
                            {currentId ? 'EDITAR AVISO' : 'CRIAR NOVO AVISO'}
                        </h2>
                        <button onClick={resetForm} className="text-noir-400 hover:text-white transition-colors">
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {error && (
                        <div className="bg-red-900/20 border border-red-500/50 text-red-500 p-3 rounded mb-4 text-sm flex items-center gap-2">
                            <Siren className="w-4 h-4" /> {error}
                        </div>
                    )}

                    <form onSubmit={handleSave} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs uppercase text-noir-muted font-bold mb-1">Título *</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="w-full bg-black/50 border border-noir-700 rounded p-3 text-white outline-none focus:border-noir-accent transition-colors"
                                        placeholder="Ex: Atualização de Servidor"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs uppercase text-noir-muted font-bold mb-1">Texto do Aviso *</label>
                                    <textarea
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        className="w-full h-32 bg-black/50 border border-noir-700 rounded p-3 text-white outline-none focus:border-noir-accent transition-colors resize-none"
                                        placeholder="Mensagem detalhada para os jogadores..."
                                        required
                                    />
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <label className="block text-xs uppercase text-noir-muted font-bold mb-1">Validade (Dias) *</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="365"
                                            value={expiresDays}
                                            onChange={(e) => setExpiresDays(Number(e.target.value))}
                                            className="w-full bg-black/50 border border-noir-700 rounded p-3 text-white outline-none focus:border-noir-accent transition-colors font-mono"
                                        />
                                    </div>
                                    <div className="flex-1 flex flex-col justify-end pb-1">
                                        <button
                                            type="button"
                                            onClick={() => setIsActive(!isActive)}
                                            className={`flex items-center gap-2 p-2 rounded border transition-colors ${isActive ? 'bg-green-900/20 border-green-500/50 text-green-400' : 'bg-red-900/20 border-red-500/50 text-red-400'}`}
                                        >
                                            {isActive ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                                            <span className="font-bold text-sm">{isActive ? 'ATIVO' : 'INATIVO'}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs uppercase text-noir-muted font-bold mb-1">Imagem / Banner (Opcional)</label>
                                    <div className="flex items-center gap-2 mb-2">
                                        <label className="bg-noir-800 hover:bg-noir-700 cursor-pointer text-white px-3 py-2 rounded flex items-center gap-2 transition-colors text-sm font-bold uppercase">
                                            <ImageIcon className="w-4 h-4" /> Enviar Imagem
                                            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                                        </label>
                                    </div>

                                    {uploadProgress > 0 && uploadProgress < 100 && (
                                        <div className="w-full bg-black rounded-full h-2 mb-2 border border-noir-800">
                                            <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
                                        </div>
                                    )}

                                    {imageUrl ? (
                                        <div className="relative group rounded overflow-hidden border border-noir-700 aspect-video bg-black flex items-center justify-center">
                                            <img src={imageUrl} alt="Preview" className="max-h-full max-w-full object-contain" />
                                            <button
                                                type="button"
                                                onClick={() => setImageUrl('')}
                                                className="absolute top-2 right-2 bg-red-600 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X className="w-4 h-4 text-white" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="aspect-video bg-black/50 border border-noir-700 border-dashed rounded flex flex-col items-center justify-center text-noir-600">
                                            <ImageIcon className="w-8 h-8 mb-2 opacity-50" />
                                            <span className="text-xs">Nenhuma imagemSelecionada</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-noir-800 flex justify-end gap-4">
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-6 py-2 rounded bg-transparent border border-noir-600 text-noir-300 hover:text-white hover:bg-noir-800 transition-colors font-bold uppercase"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 rounded bg-amber-600 hover:bg-amber-500 text-black font-bold uppercase tracking-wider flex items-center gap-2 transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Salvando...' : <><Save className="w-4 h-4" /> Salvar Aviso</>}
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {announcements.length === 0 ? (
                        <div className="col-span-full py-12 flex flex-col items-center justify-center text-noir-500 border border-noir-800 border-dashed rounded-lg bg-black/20">
                            <Megaphone className="w-12 h-12 mb-4 opacity-50" />
                            <p className="font-mono text-sm uppercase">Nenhum aviso registrado.</p>
                        </div>
                    ) : (
                        announcements.map(ann => {
                            const isExpired = ann.expiresAt && ann.expiresAt.toMillis() < Date.now();
                            const isVisible = ann.isActive && !isExpired;

                            return (
                                <div key={ann.id} className={`bg-noir-900 border ${isVisible ? 'border-amber-900/50 shadow-[0_0_15px_rgba(245,158,11,0.05)]' : 'border-noir-800 opacity-60'} rounded-lg p-4 flex flex-col relative overflow-hidden group`}>

                                    {!ann.isActive && <div className="absolute top-2 right-2 text-[10px] bg-red-900/80 text-red-200 px-2 py-0.5 rounded font-bold uppercase">Inativo</div>}
                                    {isExpired && ann.isActive && <div className="absolute top-2 right-2 text-[10px] bg-gray-800 text-gray-400 px-2 py-0.5 rounded font-bold uppercase">Expirado</div>}
                                    {isVisible && <div className="absolute top-2 right-2 text-[10px] bg-green-900/80 text-green-200 px-2 py-0.5 flex items-center gap-1 rounded font-bold uppercase"><CheckCircle className="w-3 h-3" /> No Ar</div>}

                                    <h3 className="font-bold text-gray-200 pr-16 mb-2">{ann.title}</h3>

                                    {ann.imageUrl && (
                                        <div className="w-full h-24 bg-black rounded mb-2 overflow-hidden">
                                            <img src={ann.imageUrl} className="w-full h-full object-cover opacity-70" alt="Thumb" />
                                        </div>
                                    )}

                                    <p className="text-sm text-noir-400 line-clamp-2 mb-4 flex-1">{ann.text}</p>

                                    <div className="pt-3 border-t border-noir-800 flex justify-between items-center mt-auto">
                                        <span className="text-[10px] text-noir-500 font-mono">
                                            Expira: {ann.expiresAt?.toDate().toLocaleDateString('pt-BR')}
                                        </span>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleEdit(ann)} className="p-1.5 bg-noir-800 hover:bg-noir-700 rounded text-amber-500 transition-colors">
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => handleDelete(ann.id)} className="p-1.5 bg-noir-800 hover:bg-red-900/50 rounded text-red-500 transition-colors">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};
